import React, { useState, useEffect} from "react";
import ReactDOM from "react-dom";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, horizontalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { BASE_URL } from "../../constants";
import { apiFetch } from "../../utils/apiFetch";
import AppSpinner from "../Spinner/AppSpinner";

const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  const regExp = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regExp);
  return match ? `https://www.youtube.com/embed/${match[1]}?autoplay=1` : null;
};

const isPlaylistUrl = (url) => {
  if (!url) return false;
  return /[?&]list=/.test(url) && (url.includes('playlist?') || !url.includes('v='));
};

const markColor = (val) =>
  val >= 8 ? "#059669" : val >= 5 ? "#f59e0b" : "#ef4444";


function NoteCell({ value }) {
  const [show, setShow] = useState(false);
  if (!value) return <span style={{ color: "var(--text-muted)", opacity: 0.5 }}>—</span>;

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={(e) => { e.stopPropagation(); setShow((p) => !p); }}
        style={{
          border: "1px solid var(--border)",
          background: show ? "var(--primary)" : "var(--bg-elevated)",
          cursor: "pointer",
          color: show ? "#fff" : "var(--text-secondary)",
          padding: "4px 8px",
          borderRadius: 6,
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          gap: 6,
          transition: "all 0.2s ease",
          boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
        }}
      >
        <i className={`bi bi-${show ? "chat-left-dots-fill" : "chat-left-text"}`}></i>
        <span style={{ fontWeight: 600 }}>Qeyd</span>
      </button>

      {show && (
        <div style={{
          position: "absolute", top: "120%", left: 0, zIndex: 100, minWidth: 200,
          backgroundColor: "var(--bg-elevated)", color: "var(--text-primary)",
          padding: "10px 12px", borderRadius: 8, fontSize: 12, lineHeight: "1.5",
          boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)", border: "1px solid var(--border)",
          animation: "fadeIn 0.2s ease-out"
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 10, color: "var(--primary)", textTransform: "uppercase" }}>
            Müəllimin şərhi:
          </div>
          {value}
          <div style={{
            position: "absolute", top: -5, left: 15, width: 10, height: 10,
            backgroundColor: "var(--bg-elevated)", borderLeft: "1px solid var(--border)",
            borderTop: "1px solid var(--border)", transform: "rotate(45deg)"
          }}></div>
        </div>
      )}
    </div>
  );
}

const DEFAULT_COLUMNS = [
  { id: "id", label: "Kod" },
  { id: "trainingType", label: "Format" },
  { id: "lessonType", label: "Tip" },
  { id: "present", label: "Davamiyyət" },
  { id: "markHomework", label: "Ev tapşırığı" },
  { id: "markTest", label: "Test" },
  { id: "markClasswork", label: "Sinif tapşırığı" },
  { id: "resourceLink", label: "Material" },
  { id: "youtubeLink", label: "Video" },
  { id: "teacherSelectedDate", label: "Qeyd tarixi" },
  { id: "notes", label: "Tələbə qeydi" },
  { id: "teacherNotes", label: "Müəllim qeydi" },
  { id: "lessonNotes", label: "Dərsin adı" },
];

function renderCell(colId, m, openYoutubeModal, onEditYoutubeLink) {
  switch (colId) {
    case "id":
      return <span style={{ color: "var(--text-secondary)", fontWeight: 600 }}>{m.id || "—"}</span>;
    case "trainingType":
      return (
        <span style={{
          fontSize: 11, padding: "3px 10px", borderRadius: 20, fontWeight: 700,
          background: m.trainingType === "Online" ? "rgba(5,150,105,0.12)" : "rgba(239,68,68,0.12)",
          color: m.trainingType === "Online" ? "#059669" : "#ef4444",
          border: `1px solid ${m.trainingType === "Online" ? "rgba(5,150,105,0.3)" : "rgba(239,68,68,0.3)"}`,
          display: "inline-flex", alignItems: "center", gap: 5,
        }}>
          <i className={`bi bi-${m.trainingType === "Online" ? "wifi" : "building"}`}></i> {m.trainingType}
        </span>
      );
    case "lessonType":
      return (
        <span style={{
          fontSize: 11, padding: "3px 10px", borderRadius: 20, fontWeight: 700,
          background: m.lessonType === "Standart" ? "rgba(37,99,235,0.1)" : "rgba(245,158,11,0.1)",
          color: m.lessonType === "Standart" ? "var(--primary)" : "#f59e0b",
          border: `1px solid ${m.lessonType === "Standart" ? "rgba(37,99,235,0.3)" : "rgba(245,158,11,0.3)"}`,
          display: "inline-flex", alignItems: "center", gap: 5,
        }}>
          {m.lessonType}
        </span>
      );
    case "present":
      return (
        <span style={{
          fontSize: 11, padding: "3px 10px", borderRadius: 20, fontWeight: 700,
          background: m.present ? "rgba(5,150,105,0.12)" : "rgba(239,68,68,0.12)",
          color: m.present ? "#059669" : "#ef4444",
          border: `1px solid ${m.present ? "rgba(5,150,105,0.3)" : "rgba(239,68,68,0.3)"}`,
          display: "inline-flex", alignItems: "center", gap: 5,
        }}>
          {m.present ? "İştirak etdi" : "İştirak etmədi"}
        </span>
      );
    case "markHomework":
    case "markTest":
    case "markClasswork":
      return m[colId] != null ? (
        <span style={{
          fontWeight: 800, fontSize: 12, color: markColor(m[colId]),
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          padding: "2px 7px", borderRadius: 6, background: `${markColor(m[colId])}18`,
          whiteSpace: "nowrap",
        }}>
          {m[colId]}/10
        </span>
      ) : <span style={{ color: "#9ca3af" }}>—</span>;
    case "resourceLink":
      return m.resourceLink ? (
        <a href={m.resourceLink} target="_blank" rel="noreferrer" className="btn-link-custom"><i className="bi bi-folder2-open"></i></a>
      ) : "—";
    case "youtubeLink":
      return m.youtubeLink ? (
        <button onClick={(e) => { e.stopPropagation(); openYoutubeModal(m.youtubeLink); }} className="btn-link-custom" style={{ color: "#ef4444", background: "rgba(239,68,68,0.12)", borderColor: "rgba(239,68,68,0.25)" }}><i className="bi bi-play-circle-fill"></i></button>
      ) : (
        <button
          onClick={(e) => { e.stopPropagation(); onEditYoutubeLink && onEditYoutubeLink(m.id); }}
          title="Video link əlavə et"
          style={{
            border: "1px dashed #d1d5db", borderRadius: 6, background: "transparent",
            cursor: "pointer", padding: "3px 8px", color: "#9ca3af", fontSize: 11,
            display: "flex", alignItems: "center", gap: 4, transition: "all 0.15s"
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "#ef4444"; e.currentTarget.style.color = "#ef4444"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "#d1d5db"; e.currentTarget.style.color = "#9ca3af"; }}
        >
          <i className="bi bi-plus-circle"></i> Link əlavə et
        </button>
      );
    case "teacherSelectedDate":
      return (
        <span style={{ color: "var(--text-secondary)", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 5 }}>
          <i className="bi bi-calendar3" style={{ color: "var(--primary)", fontSize: 12 }}></i>
          {m.teacherSelectedDate ? new Date(m.teacherSelectedDate).toLocaleString("az-AZ") : "—"}
        </span>
      );
    case "teacherNotes": return <NoteCell value={m.teacherNotes} />;
    case "notes":
      return <span style={{ fontSize: 11 }}>{Array.isArray(m.notes) ? m.notes.map(n => n.name).join(", ") || "—" : m.notes || "—"}</span>;
    default: return <span>{m[colId] || "—"}</span>;
  }
}

function getCellText(colId, m) {
  switch (colId) {
    case "present": return m.present ? "İştirak etdi" : "İştirak etmədi";
    case "teacherSelectedDate": return m.teacherSelectedDate ? new Date(m.teacherSelectedDate).toLocaleString("az-AZ") : "";
    case "resourceLink": return m.resourceLink || "";
    case "youtubeLink": return m.youtubeLink || "";
    default: return m[colId] != null ? String(m[colId]) : "";
  }
}

function SortableCol({ col, marks, onCellClick, openYoutubeModal, onEditYoutubeLink }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: col.id });
  const minWidth = col.id === "lessonNotes" ? 220 : col.id === "notes" || col.id === "teacherNotes" ? 180 : 130;

  return (
    <div ref={setNodeRef} style={{
      transform: CSS.Transform.toString(transform),
      transition, opacity: isDragging ? 0.5 : 1, display: "flex",
      flexDirection: "column", minWidth, backgroundColor: "var(--bg-main)", borderRight: "1px solid var(--border)",
    }}>
      <div {...attributes} {...listeners} style={{
        padding: "12px", background: "var(--bg-elevated)", color: "var(--text-muted)", fontSize: 10,
        fontWeight: 700, textTransform: "uppercase", borderBottom: "2px solid var(--border)",
        cursor: "grab", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, height: "45px", position: "sticky", top: 0, zIndex: 10
      }}>
        <i className="bi bi-grip-vertical" style={{ opacity: 0.4 }}></i> {col.label}
      </div>
      {marks.map((m) => (
        <div
          key={m.id}
          onClick={() => onCellClick?.(getCellText(col.id, m))}
          style={{ padding: "8px 12px", fontSize: 12, borderBottom: "1px solid var(--border)", height: "55px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
        >
          {renderCell(col.id, m, openYoutubeModal, onEditYoutubeLink)}
        </div>
      ))}
    </div>
  );
}

// ASAS PANEL KOMPONENTİ
export default function MarksPanel({ marks = [], onFilterChange, currentDates, onCellClick, saveRef, refreshRef, marksVersion, onVersionChange, loading, onApiError }) {
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [youtubeModal, setYoutubeModal] = useState(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const [editYoutube, setEditYoutube] = useState(null); // { markId, value }
  const [youtubeSaving, setYoutubeSaving] = useState(false);
  const [youtubeLinkOverrides, setYoutubeLinkOverrides] = useState({});

  const handleSaveYoutubeLink = async () => {
    if (!editYoutube || !editYoutube.value.trim()) return;
    setYoutubeSaving(true);
    try {
      const endpoint = marksVersion === "v2"
        ? `${BASE_URL}/student-marks/update-youtube-link/v2`
        : `${BASE_URL}/student-marks/update-youtube-link`;
      const res = await apiFetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ markId: editYoutube.markId, youtubeLink: editYoutube.value.trim() }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (onApiError) onApiError({ message: data?.message ?? 'YouTube link əlavə edilərkən xəta baş verdi', validations: data?.validations });
        else window.dispatchEvent(new CustomEvent('api-error', { detail: { message: data?.message ?? 'YouTube link əlavə edilərkən xəta baş verdi' } }));
        return;
      }
      setYoutubeLinkOverrides(prev => ({ ...prev, [editYoutube.markId]: editYoutube.value.trim() }));
      setEditYoutube(null);
      window.dispatchEvent(new CustomEvent('api-success', { detail: { message: 'YouTube link uğurla əlavə edildi' } }));
    } catch (e) {
      console.error('YouTube link yenilənmədi:', e);
      if (onApiError) onApiError({ message: 'YouTube link əlavə edilərkən xəta baş verdi' });
      else window.dispatchEvent(new CustomEvent('api-error', { detail: { message: 'YouTube link əlavə edilərkən xəta baş verdi' } }));
    } finally {
      setYoutubeSaving(false);
    }
  };

  const openYoutubeModal = async (url) => {
    const playlist = isPlaylistUrl(url);
    setIframeLoaded(false);
    if (playlist) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    } else {
      setYoutubeModal({ url, title: 'Video', isPlaylist: false });
      try {
        const res = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`);
        if (res.ok) {
          const json = await res.json();
          setYoutubeModal(prev => prev ? { ...prev, title: json.title || 'Video' } : null);
        }
      } catch { }
    }
  };

  const closeYoutubeModal = () => { setYoutubeModal(null); setIframeLoaded(false); };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeYoutubeModal(); };
    if (youtubeModal) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [youtubeModal]);

  // Local inputlar üçün state (StudentTab-dan gələn tarixləri başlanğıc dəyər edirik)
  const [tempDates, setTempDates] = useState({
    beginDate: currentDates?.beginDate || "",
    endDate: currentDates?.endDate || ""
  });

  useEffect(() => {
    const saved = localStorage.getItem("marksTableOrder");
    if (saved) {
      try { setColumns(JSON.parse(saved)); } catch (e) { console.error(e); }
    }
  }, []);

  useEffect(() => {
    if (saveRef) saveRef.current = () => {
      localStorage.setItem("marksTableOrder", JSON.stringify(columns));
    };
  }, [columns, saveRef]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // "Göstər" düyməsi basılanda StudentTab-a xəbər veririk
  const handleApply = () => {
    if (onFilterChange) {
      onFilterChange({
        beginDate: tempDates.beginDate,
        endDate: tempDates.endDate
      });
    }
  };

  useEffect(() => {
    if (refreshRef) refreshRef.current = handleApply;
  });

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumns((cols) => {
        const oldIndex = cols.findIndex((c) => c.id === active.id);
        const newIndex = cols.findIndex((c) => c.id === over.id);
        return arrayMove(cols, oldIndex, newIndex);
      });
    }
  };

  return (
    <div style={{ padding: "10px", display: "flex", flexDirection: "column", minHeight: "100%", height: "100%", overflow: "hidden", boxSizing: "border-box" }}>
      
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", flexWrap: "wrap", gap: "10px" }}>
        <div className="filter-panel-custom">
          {onVersionChange && (
            <div style={{ display: "flex", border: "1px solid #d1d5db", borderRadius: 7, overflow: "hidden", height: 32, marginRight: 8 }}>
              <button
                onClick={() => onVersionChange("v1")}
                style={{
                  padding: "0 12px", fontSize: 11, fontWeight: 700, border: "none", cursor: "pointer",
                  background: marksVersion === "v1" ? "#198754" : "#f9fafb",
                  color: marksVersion === "v1" ? "#fff" : "#6b7280",
                  transition: "all 0.15s",
                }}
              >
                Köhnə
              </button>
              <button
                onClick={() => onVersionChange("v2")}
                style={{
                  padding: "0 12px", fontSize: 11, fontWeight: 700, border: "none", borderLeft: "1px solid #d1d5db", cursor: "pointer",
                  background: marksVersion === "v2" ? "#198754" : "#f9fafb",
                  color: marksVersion === "v2" ? "#fff" : "#6b7280",
                  transition: "all 0.15s",
                }}
              >
                Yeni
              </button>
            </div>
          )}
          <div className="filter-inputs-wrapper">
            <div className="date-input-group">
              <label>BAŞLANĞIC</label>
              <input 
                type="date" 
                value={tempDates.beginDate} 
                onChange={(e) => setTempDates({...tempDates, beginDate: e.target.value})} 
              />
            </div>
            <div className="date-input-group">
              <label>SON TARİX</label>
              <input 
                type="date" 
                value={tempDates.endDate} 
                onChange={(e) => setTempDates({...tempDates, endDate: e.target.value})} 
              />
            </div>
          </div>
          <button onClick={handleApply} className="btn-show">Göstər</button>
        </div>

      </div>

      <div className="custom-table-outer">
        <div className="custom-table-container">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={columns.map((c) => c.id)} strategy={horizontalListSortingStrategy}>
              <div style={{ display: "flex", width: "max-content", minWidth: "100%" }}>
                {columns.map((col) => (
                  <SortableCol
                    key={col.id} col={col}
                    marks={marks.map(m => youtubeLinkOverrides[m.id] !== undefined ? { ...m, youtubeLink: youtubeLinkOverrides[m.id] } : m)}
                    onCellClick={onCellClick}
                    openYoutubeModal={openYoutubeModal}
                    onEditYoutubeLink={(markId) => setEditYoutube({ markId, value: "" })}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>

      {editYoutube && ReactDOM.createPortal(
        <div
          onClick={() => setEditYoutube(null)}
          style={{ position: "fixed", inset: 0, zIndex: 100060, background: "rgba(15, 23, 42, 0.45)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 14, boxShadow: "0 24px 64px rgba(0,0,0,0.2)", padding: "24px", width: 480, maxWidth: "95vw" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", display: "flex", alignItems: "center", gap: 8 }}>
                <i className="bi bi-youtube" style={{ color: "#ef4444", fontSize: 18 }}></i>
                YouTube Link əlavə et
              </div>
              <button
                onClick={() => setEditYoutube(null)}
                style={{ border: "none", background: "#e5e7eb", borderRadius: 6, width: 28, height: 28, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280", fontSize: 16 }}
              >×</button>
            </div>
            <input
              autoFocus
              type="url"
              placeholder="https://youtube.com/watch?v=..."
              value={editYoutube.value}
              onChange={e => setEditYoutube(prev => ({ ...prev, value: e.target.value }))}
              onKeyDown={e => { if (e.key === "Enter") handleSaveYoutubeLink(); if (e.key === "Escape") setEditYoutube(null); }}
              style={{ width: "100%", border: "1.5px solid #d1d5db", borderRadius: 8, padding: "10px 12px", fontSize: 13, outline: "none", marginBottom: 16, boxSizing: "border-box" }}
              onFocus={e => { e.target.style.borderColor = "#ef4444"; }}
              onBlur={e => { e.target.style.borderColor = "#d1d5db"; }}
            />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => setEditYoutube(null)}
                style={{ border: "1.5px solid #d1d5db", borderRadius: 7, padding: "7px 18px", background: "#f9fafb", color: "#374151", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
              >Ləğv et</button>
              <button
                onClick={handleSaveYoutubeLink}
                disabled={youtubeSaving || !editYoutube.value.trim()}
                style={{ border: "none", borderRadius: 7, padding: "7px 20px", background: youtubeSaving || !editYoutube.value.trim() ? "#fca5a5" : "#ef4444", color: "#fff", fontSize: 12, fontWeight: 700, cursor: youtubeSaving || !editYoutube.value.trim() ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6 }}
              >
                {youtubeSaving ? <AppSpinner inline /> : <><i className="bi bi-check-lg"></i>Saxla</>}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {youtubeModal?.url && (
        <div
          onClick={closeYoutubeModal}
          style={{
            position: 'fixed', inset: 0, zIndex: 999999,
            background: '#0f172a',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
            animation: 'yt-fade-in 0.2s ease',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '92vw', height: '88vh',
              display: 'flex', flexDirection: 'column',
              borderRadius: '16px', overflow: 'hidden',
              boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
              animation: 'yt-scale-in 0.22s cubic-bezier(0.34,1.56,0.64,1)',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 16px', background: '#fff',
              borderBottom: '1px solid #f3f4f6', flexShrink: 0,
            }}>
              <span style={{ color: '#111827', fontWeight: '700', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                <span style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <i className={`bi bi-${youtubeModal.isPlaylist ? 'collection-play-fill' : 'play-circle-fill'}`} style={{ color: '#ef4444', fontSize: 14 }}></i>
                </span>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {youtubeModal.title}
                </span>
                {youtubeModal.isPlaylist && (
                  <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 20, padding: '2px 8px' }}>
                    Playlist
                  </span>
                )}
              </span>
              <button onClick={closeYoutubeModal}
                style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '5px 12px', display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', color: '#6b7280', fontSize: '12px', fontWeight: '700' }}>
                ✕ Bağla
              </button>
            </div>
            {/* Playlist — video seçilməyib: yalnız siyahı */}
            {youtubeModal.isPlaylist && !youtubeModal.currentVideoId ? (
              <div style={{ flex: 1, background: '#0f0f0f', overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: '#333 #0f0f0f' }}>
                {youtubeModal.playlistVideos === null ? (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: '3px solid rgba(37,99,235,0.15)', borderTopColor: '#2563eb', animation: 'yt-spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                      <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>Yüklənir...</span>
                    </div>
                  </div>
                ) : youtubeModal.playlistVideos.length === 0 ? (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.35)', fontSize: 13 }}>
                    Videolar tapılmadı
                  </div>
                ) : (
                  <div style={{ padding: '16px 20px' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>
                      <i className="bi bi-collection-play" style={{ marginRight: 6 }}></i>
                      {youtubeModal.playlistVideos.length} video — birini seçin
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                      {youtubeModal.playlistVideos.map((v, i) => (
                        <div
                          key={v.id}
                          onClick={() => { setYoutubeModal(prev => prev ? { ...prev, currentVideoId: v.id } : null); setIframeLoaded(false); }}
                          style={{ cursor: 'pointer', borderRadius: 10, overflow: 'hidden', background: '#1a1a1a', border: '1px solid #2a2a2a', transition: 'transform 0.15s, border-color 0.15s' }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = '#2563eb'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = '#2a2a2a'; }}
                        >
                          <div style={{ position: 'relative' }}>
                            <img src={v.thumbnail} alt="" style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }} />
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0)', transition: 'background 0.2s' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.4)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0)'}
                            >
                              <i className="bi bi-play-circle-fill" style={{ color: 'rgba(255,255,255,0.9)', fontSize: 32, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))' }}></i>
                            </div>
                            <span style={{ position: 'absolute', bottom: 5, right: 6, fontSize: 10, fontWeight: 700, background: 'rgba(0,0,0,0.8)', color: '#fff', padding: '2px 6px', borderRadius: 4 }}>{i + 1}</span>
                          </div>
                          <div style={{ padding: '8px 10px' }}>
                            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {v.title}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Video seçilib və ya adi video: player + yan siyahı */
              <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                <div style={{ flex: 1, position: 'relative', background: '#000' }}>
                  {!iframeLoaded && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f0f0f' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: '3px solid rgba(37,99,235,0.15)', borderTopColor: '#2563eb', animation: 'yt-spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>Yüklənir...</span>
                      </div>
                    </div>
                  )}
                  <iframe
                    title="Dərs videosu"
                    src={
                      youtubeModal.isPlaylist
                        ? `https://www.youtube.com/embed/${youtubeModal.currentVideoId}?autoplay=1`
                        : getYoutubeEmbedUrl(youtubeModal.url)
                    }
                    style={{ width: '100%', height: '100%', border: 'none', opacity: iframeLoaded ? 1 : 0, transition: 'opacity 0.3s ease' }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                    onLoad={() => setIframeLoaded(true)}
                  />
                </div>
                {youtubeModal.isPlaylist && youtubeModal.playlistVideos?.length > 0 && (
                  <div style={{ width: 260, overflowY: 'auto', background: '#0f0f0f', borderLeft: '1px solid #222', flexShrink: 0, scrollbarWidth: 'thin', scrollbarColor: '#333 #0f0f0f' }}>
                    <div style={{ padding: '8px 12px', fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', borderBottom: '1px solid #222', letterSpacing: 1 }}>
                      <i className="bi bi-collection-play" style={{ marginRight: 6 }}></i>
                      {youtubeModal.playlistVideos.length} video
                    </div>
                    {youtubeModal.playlistVideos.map((v, i) => {
                      const active = v.id === youtubeModal.currentVideoId;
                      return (
                        <div
                          key={v.id}
                          onClick={() => { setYoutubeModal(prev => prev ? { ...prev, currentVideoId: v.id } : null); setIframeLoaded(false); }}
                          style={{
                            display: 'flex', gap: 8, padding: '8px 10px', cursor: 'pointer', alignItems: 'flex-start',
                            background: active ? 'rgba(37,99,235,0.18)' : 'transparent',
                            borderLeft: active ? '3px solid #2563eb' : '3px solid transparent',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                          onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            <img src={v.thumbnail} alt="" style={{ width: 80, height: 46, objectFit: 'cover', borderRadius: 5, display: 'block' }} />
                            {active && (
                              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)', borderRadius: 5 }}>
                                <i className="bi bi-play-fill" style={{ color: '#fff', fontSize: 16 }}></i>
                              </div>
                            )}
                            <span style={{ position: 'absolute', bottom: 3, right: 3, fontSize: 9, fontWeight: 700, background: 'rgba(0,0,0,0.75)', color: '#fff', padding: '1px 4px', borderRadius: 3 }}>{i + 1}</span>
                          </div>
                          <span style={{ fontSize: 11, color: active ? '#fff' : 'rgba(255,255,255,0.7)', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {v.title}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        .spin-icon { display: inline-block; animation: spin 0.8s linear infinite; }
        @keyframes yt-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes yt-scale-in { from { transform: scale(0.93); opacity: 0 } to { transform: scale(1); opacity: 1 } }
        @keyframes yt-spin { to { transform: rotate(360deg) } }
        .filter-panel-custom { display: flex; align-items: flex-end; gap: 15px; padding: 12px; background: var(--bg-elevated); border-radius: 10px; border: 1px solid var(--border); }
        .filter-inputs-wrapper { display: flex; gap: 10px; }
        .date-input-group { display: flex; flex-direction: column; gap: 4px; }
        .date-input-group label { font-size: 9px; font-weight: 800; color: var(--text-muted); }
        .date-input-group input { padding: 6px 10px; border-radius: 6px; border: 1px solid var(--border); background: var(--bg-main); color: var(--text-primary); font-size: 13px; outline: none; }
        .btn-show { background: var(--primary); color: white; border: none; padding: 8px 16px; border-radius: 8px; font-weight: 600; cursor: pointer; }
        
        .btn-save-icon { 
          width: 42px; height: 42px; display: flex; align-items: center; justify-content: center;
          background: #059669; color: white; border: none; border-radius: 50%; 
          font-size: 18px; cursor: pointer; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
        }
        .btn-save-icon.active { background: #2563eb; transform: scale(1.1); }
        .btn-save-icon:hover { opacity: 0.9; transform: translateY(-2px); }

        .custom-table-outer { flex: 1; min-height: 0; display: flex; flex-direction: column; }
        .custom-table-container { overflow: auto; flex: 1; border: 1px solid var(--border); border-radius: 12px; background: var(--bg-main); scrollbar-width: thin; scrollbar-color: #93c5fd #eff6ff; }
        .custom-table-container::-webkit-scrollbar { height: 6px; width: 5px; }
        .custom-table-container::-webkit-scrollbar-track { background: #eff6ff; border-radius: 3px; }
        .custom-table-container::-webkit-scrollbar-thumb { background: #93c5fd; border-radius: 3px; }
        .custom-table-container::-webkit-scrollbar-thumb:hover { background: #60a5fa; }

        .btn-link-custom {
          display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px;
          border-radius: 8px; background: rgba(37,99,235,0.08); color: var(--color-primary); border: 1px solid rgba(37,99,235,0.2); text-decoration: none; cursor: pointer;
        }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}